import { useState, useEffect, useRef } from "react";
import { Bot, Loader2 } from "lucide-react";

import { useChatbot } from "@/hooks/useChatbot";
import { ChatbotHeader } from "@/components/chatbot/ChatbotHeader";
import { ChatMessage } from "@/components/chatbot/ChatMessage";
import { SuggestedQuestions } from "@/components/chatbot/SuggestedQuestions";
import { ChatbotInput } from "@/components/chatbot/ChatbotInput";

/**
 * Floating chatbot widget. Presentational shell — all state/logic lives in
 * useChatbot. Sub-components are kept flat to avoid the Emergent visual-edits
 * Babel plugin crashing on deeply nested JSX trees.
 */
export const ChatbotWidget = () => {
  const [open, setOpen] = useState(false);
  const { messages, loading, send } = useChatbot();
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Ouvrir le chatbot Mediastore"
        data-testid="chatbot-open-btn"
        className="fixed z-50 bottom-6 right-24 md:right-24 flex items-center justify-center w-14 h-14 rounded-full bg-red-600 hover:bg-red-700 text-white shadow-lg transition-all hover:scale-110"
      >
        <Bot className="h-7 w-7" />
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
        </span>
      </button>
    );
  }

  const showSuggestions = messages.length <= 1 && !loading;

  return (
    <div
      data-testid="chatbot-panel"
      className="fixed z-50 bottom-6 right-6 left-6 sm:left-auto sm:bottom-6 sm:right-6 sm:w-[380px] max-h-[80vh] flex flex-col rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
    >
      <ChatbotHeader onClose={() => setOpen(false)} />

      <div
        ref={scrollRef}
        data-testid="chatbot-messages"
        className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50 min-h-[280px]"
      >
        {messages.map((m) => (
          <ChatMessage key={m.id} role={m.role} content={m.content} />
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>L&apos;assistant rédige…</span>
          </div>
        )}
      </div>

      {showSuggestions && <SuggestedQuestions onPick={send} />}

      <ChatbotInput onSend={send} disabled={loading} autoFocus={open} />
    </div>
  );
};
